import type { AnyItem, DailyLog, ModuleKey, ScheduleWindow, TaskSchedule } from "../types";
import { isoNow, localDateKey } from "./date";
import { newId } from "./id";

export const DEFAULT_OCCURRENCE_ID = "default";

export interface DueOccurrence {
  key: string;
  category: ModuleKey;
  itemId: string;
  occurrenceId: string;
  title: string;
  subtitle: string;
  date: string;
  startTime: string;
  endTime: string;
  label: string;
  notify: boolean;
  done: boolean;
  status: "done" | "missed" | "open" | "upcoming";
}

export function minutesFromTime(time: string) {
  const [hour, minute] = time.split(":").map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0;
  return hour * 60 + minute;
}

export function timeLabel(start: string, end: string) {
  return `${start}-${end}`;
}

export function defaultWindow(label = "Reminder", start = "09:00", end = "10:00"): ScheduleWindow {
  return {
    id: DEFAULT_OCCURRENCE_ID,
    label,
    start_time: start,
    end_time: end,
    notify: true,
  };
}

export function defaultSchedule(
  frequency: TaskSchedule["frequency"],
  startDate: string,
  options?: Partial<Pick<TaskSchedule, "interval_days" | "weekdays" | "windows">>,
): TaskSchedule {
  return {
    frequency,
    interval_days: Math.max(1, options?.interval_days ?? 1),
    weekdays: options?.weekdays?.length ? options.weekdays : [],
    start_date: startDate,
    windows: normalizeWindows(options?.windows),
  };
}

export function normalizeWindows(windows: unknown): ScheduleWindow[] {
  if (!Array.isArray(windows) || windows.length === 0) {
    return [defaultWindow()];
  }

  return windows.map((entry, index) => {
    const record = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {};
    const start = typeof record.start_time === "string" ? record.start_time : "09:00";
    const end = typeof record.end_time === "string" ? record.end_time : "10:00";
    return {
      id: typeof record.id === "string" && record.id ? record.id : index === 0 ? DEFAULT_OCCURRENCE_ID : newId(),
      label: typeof record.label === "string" && record.label ? record.label : index === 0 ? "Reminder" : `Reminder ${index + 1}`,
      start_time: start,
      end_time: end,
      notify: typeof record.notify === "boolean" ? record.notify : true,
    };
  });
}

export function normalizeSchedule(
  value: unknown,
  fallback: TaskSchedule,
): TaskSchedule {
  const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const frequency =
    record.frequency === "interval" || record.frequency === "weekly" || record.frequency === "daily"
      ? record.frequency
      : fallback.frequency;
  const interval =
    typeof record.interval_days === "number" && Number.isFinite(record.interval_days)
      ? record.interval_days
      : fallback.interval_days;
  const weekdays = Array.isArray(record.weekdays)
    ? record.weekdays.filter((day): day is number => Number.isInteger(day) && day >= 0 && day <= 6)
    : fallback.weekdays;

  return {
    frequency,
    interval_days: Math.max(1, interval),
    weekdays,
    start_date: typeof record.start_date === "string" && record.start_date ? record.start_date : fallback.start_date,
    windows: normalizeWindows(record.windows ?? fallback.windows),
  };
}

export function dateKeyToDate(dateKey: string) {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(year, (month || 1) - 1, day || 1));
}

export function dayDiff(startDate: string, date: string) {
  const start = dateKeyToDate(startDate).getTime();
  const end = dateKeyToDate(date).getTime();
  return Math.floor((end - start) / 86_400_000);
}

export function weekdayForDate(date: string) {
  return dateKeyToDate(date).getUTCDay();
}

export function isScheduledOn(schedule: TaskSchedule, date: string) {
  if (schedule.frequency === "daily") return true;
  if (schedule.frequency === "weekly") return schedule.weekdays.includes(weekdayForDate(date));

  const diff = dayDiff(schedule.start_date, date);
  return diff >= 0 && diff % Math.max(1, schedule.interval_days) === 0;
}

export function logKey(date: string, category: ModuleKey, itemId: string, occurrenceId = DEFAULT_OCCURRENCE_ID) {
  return `${date}_${category}_${itemId}_${occurrenceId}`;
}

export function logLookupKey(
  date: string,
  category: ModuleKey,
  itemId: string,
  occurrenceId = DEFAULT_OCCURRENCE_ID,
) {
  return logKey(date, category, itemId, occurrenceId || DEFAULT_OCCURRENCE_ID);
}

export function buildLogLookup(logs: DailyLog[]) {
  return new Set(
    logs.map((log) =>
      logLookupKey(
        log.log_date,
        log.category,
        log.item_id,
        log.occurrence_id || DEFAULT_OCCURRENCE_ID,
      ),
    ),
  );
}

export function isLogged(
  logs: DailyLog[],
  date: string,
  category: ModuleKey,
  itemId: string,
  occurrenceId = DEFAULT_OCCURRENCE_ID,
) {
  return logs.some(
    (log) =>
      log.log_date === date &&
      log.category === category &&
      log.item_id === itemId &&
      (log.occurrence_id || DEFAULT_OCCURRENCE_ID) === occurrenceId,
  );
}

export function buildLog(
  date: string,
  category: ModuleKey,
  itemId: string,
  occurrenceId = DEFAULT_OCCURRENCE_ID,
  userId?: string | null,
): DailyLog {
  return {
    id: logKey(date, category, itemId, occurrenceId),
    user_id: userId ?? null,
    log_date: date,
    category,
    item_id: itemId,
    occurrence_id: occurrenceId,
    created_at: isoNow(),
  };
}

export function occurrenceStatus(
  window: ScheduleWindow,
  done: boolean,
  nowMinutes: number,
): DueOccurrence["status"] {
  if (done) return "done";
  if (nowMinutes > minutesFromTime(window.end_time)) return "missed";
  if (nowMinutes >= minutesFromTime(window.start_time)) return "open";
  return "upcoming";
}

export function occurrencesForItem({
  category,
  item,
  title,
  subtitle,
  date,
  logs,
  loggedKeys,
  now = new Date(),
  timezone,
}: {
  category: ModuleKey;
  item: AnyItem;
  title: string;
  subtitle: string;
  date: string;
  logs: DailyLog[];
  loggedKeys?: ReadonlySet<string>;
  now?: Date;
  timezone?: string;
}): DueOccurrence[] {
  if (!isScheduledOn(item.schedule, date)) return [];

  const nowDate = localDateKey(now, timezone);
  const currentMinutes =
    nowDate === date
      ? now.getHours() * 60 + now.getMinutes()
      : nowDate > date
        ? 24 * 60
        : -1;

  return item.schedule.windows.map((window) => {
    const done = loggedKeys
      ? loggedKeys.has(logLookupKey(date, category, item.id, window.id))
      : isLogged(logs, date, category, item.id, window.id);
    const status = occurrenceStatus(window, done, currentMinutes);
    return {
      key: logKey(date, category, item.id, window.id),
      category,
      itemId: item.id,
      occurrenceId: window.id,
      title,
      subtitle,
      date,
      startTime: window.start_time,
      endTime: window.end_time,
      label: window.label,
      notify: window.notify,
      done,
      status,
    };
  });
}

export function scheduleSummary(schedule: TaskSchedule) {
  const windows = schedule.windows.map((window) => `${window.label} ${timeLabel(window.start_time, window.end_time)}`).join(", ");
  if (schedule.frequency === "daily") return `Daily · ${windows}`;
  if (schedule.frequency === "weekly") {
    const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const days = schedule.weekdays.map((day) => labels[day]).join(", ") || "No days";
    return `${days} · ${windows}`;
  }
  return `Every ${schedule.interval_days}d from ${schedule.start_date} · ${windows}`;
}
